<script setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
  onboardingInvitesSent,
  onboardingSuccessCtaClicked,
  onboardingSuccessSeen,
} from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import Dialog from "@/components-ng/ui/dialog/Dialog.vue";
import DialogContent from "@/components-ng/ui/dialog/DialogContent.vue";
import DialogDescription from "@/components-ng/ui/dialog/DialogDescription.vue";
import DialogTitle from "@/components-ng/ui/dialog/DialogTitle.vue";
import { TeamHandler } from "@/handlers/teamHandler";
import { appBoardUrl } from "@/utils/url";
import { validateEmail } from "@/utils/validation";

import BaseButton from "../BaseButton.vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  sessionId: { type: String, default: "" },
  sessionName: { type: String, default: "" },
  teamId: { type: Number, required: true },
});
const emit = defineEmits(["update:open"]);

const router = useRouter();
const teamHandler = new TeamHandler();
const team = ref(null);
const emailText = ref("");
const emailInputInvalid = ref(false);
const inviteError = ref("");
const inviteSuccess = ref("");
const sendingInvites = ref(false);

// Primary CTA opens the new board directly for the RTE (who is authenticated).
// `from=registration` tells pi-web to continue the app-owned PostHog session recording.
const boardUrl = computed(() =>
  props.sessionId ? appBoardUrl(props.sessionId, "program", { from: "registration" }) : "",
);
const canSendInvites = computed(() => emailText.value.trim().length > 0);

const editSessionHref = computed(() => {
  if (!props.sessionId) return "";
  return router.resolve({
    name: "editSession",
    query: { path: "generalInformation", id: props.sessionId },
  }).href;
});

function onOpenChange(v) {
  emit("update:open", v);
}

function parseEmails(value) {
  return [
    ...new Set(
      value
        .split(/\r?\n/)
        .map((email) => email.trim())
        .filter(Boolean),
    ),
  ];
}

function onEmailInput(value) {
  emailText.value = value;
  emailInputInvalid.value = false;
  inviteError.value = "";
  inviteSuccess.value = "";
}

async function sendInvitations() {
  const emails = parseEmails(emailText.value);
  const hasInvalidEmail = emails.some((email) => !validateEmail(email));
  if (!emails.length || hasInvalidEmail) {
    emailInputInvalid.value = true;
    inviteError.value = hasInvalidEmail
      ? "Some addresses don't look like valid emails."
      : "Add at least one email address.";
    return;
  }

  sendingInvites.value = true;
  inviteSuccess.value = "";
  inviteError.value = "";

  try {
    // `updateTeam` reconciles the team to the exact member list it's given —
    // any existing member omitted from the payload gets removed. There's no
    // additive invite endpoint, and `getTeam` doesn't include members, so we
    // load them via `getTeamMembers` and merge new invitees onto them.
    const [teamRes, membersRes] = await Promise.all([
      teamHandler.getTeam(props.teamId),
      teamHandler.getTeamMembers(props.teamId),
    ]);
    // Guard members too: getTeamMembers goes through catched() and never throws —
    // on failure it resolves with data undefined. updateTeam reconciles to the exact
    // list given, so proceeding with an empty members[] would wipe the team's roster.
    if (!teamRes.success || !teamRes.data || !membersRes.success) {
      throw new Error("Could not load team details.");
    }
    if (teamRes.data.oidc_group) {
      throw new Error(
        `Members of ${teamRes.data.name} are managed via OpenID Connect group "${teamRes.data.oidc_group}". Add users in your identity provider.`,
      );
    }
    team.value = { ...teamRes.data, members: membersRes.data || [] };

    const seen = new Set(
      (team.value.members || []).map((m) => m.email?.toLowerCase()).filter(Boolean),
    );
    const newMembers = [];
    for (const email of emails) {
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      newMembers.push({ email });
    }

    if (newMembers.length === 0) {
      inviteSuccess.value = "Those teammates are already on the team.";
      emailText.value = "";
      return;
    }

    const updatedMembers = [...(team.value.members || []), ...newMembers];
    await teamHandler.updateTeam(
      props.teamId,
      team.value.name,
      null,
      updatedMembers,
      team.value.art_id,
    );
    team.value = { ...team.value, members: updatedMembers };
    trackEvent(onboardingInvitesSent(newMembers.length));
    inviteSuccess.value = `${newMembers.length} invitation${newMembers.length === 1 ? "" : "s"} sent!`;
    emailText.value = "";
  } catch (e) {
    inviteError.value = e.userMessage || e.message || "Could not send invitations.";
  } finally {
    sendingInvites.value = false;
  }
}

function resetInviteState() {
  team.value = null;
  emailText.value = "";
  emailInputInvalid.value = false;
  inviteError.value = "";
  inviteSuccess.value = "";
  sendingInvites.value = false;
}

watch(
  () => props.open,
  (open) => {
    if (open) trackEvent(onboardingSuccessSeen());
    else resetInviteState();
  },
  { immediate: true },
);
</script>

<template>
  <Dialog :open @update:open="onOpenChange">
    <DialogContent class="onboard-success-dialog">
      <header class="head">
        <DialogTitle class="title">Your PI is ready to view</DialogTitle>
        <DialogDescription class="sub">
          "{{ sessionName }}" is set up. Open the board to review it now.
        </DialogDescription>
      </header>

      <section class="invite-rail" aria-labelledby="invite-title">
        <div class="invite-head">
          <div class="invite-copy">
            <h2 id="invite-title" class="invite-title">Invite teammates</h2>
            <p class="invite-note">You can also add them later from team settings.</p>
          </div>
          <p class="invite-sub">Optional</p>
        </div>
        <div class="invite-composer">
          <textarea
            class="invite-input"
            :class="{ error: emailInputInvalid }"
            :value="emailText"
            rows="3"
            placeholder="jane@company.com&#10;bob@company.com"
            aria-label="Teammate email addresses"
            :aria-invalid="emailInputInvalid ? 'true' : 'false'"
            @input="onEmailInput($event.target.value)"
          />
          <div class="invite-actions">
            <p v-if="inviteError" class="invite-message error" role="alert">{{ inviteError }}</p>
            <p v-else-if="inviteSuccess" class="invite-message success" role="status">
              {{ inviteSuccess }}
            </p>
            <p v-else class="invite-message hint">Add one email per line.</p>
            <BaseButton
              variant="outlined"
              color="grey"
              size="small"
              :disabled="!canSendInvites"
              :loading="sendingInvites"
              @click="sendInvitations"
            >
              Send invites
            </BaseButton>
          </div>
        </div>
      </section>

      <footer class="actions">
        <BaseButton
          as="a"
          :href="editSessionHref"
          target="_blank"
          rel="noopener"
          variant="ghost"
          color="grey"
          @click="trackEvent(onboardingSuccessCtaClicked('customize-session'))"
        >
          Customize PI Session
        </BaseButton>
        <BaseButton
          as="a"
          :href="boardUrl"
          target="_blank"
          rel="noopener"
          variant="filled"
          color="primary"
          @click="trackEvent(onboardingSuccessCtaClicked('view-app'))"
        >
          View your PI in the app
        </BaseButton>
      </footer>
    </DialogContent>
  </Dialog>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/mixins/typography";

:global(.onboard-success-dialog) {
  max-width: 560px;
  padding: 40px 44px 32px;
}

.head {
  margin-bottom: 24px;
  padding-right: 32px;
}

.title {
  @include typography.heading;

  color: c.$black;
  margin: 0 0 8px;
}

.sub {
  @include typography.medium;

  color: c.$text-subtle;
  margin: 0;
}

.invite-rail {
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid c.$border-subtle;
}

.invite-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.invite-copy {
  min-width: 0;
}

.invite-title {
  @include typography.medium("semi-bold");

  color: c.$black;
  margin: 0;
}

.invite-note {
  @include typography.small;

  color: c.$text-subtle;
  margin: 3px 0 0;
}

.invite-sub {
  @include typography.extra-small("medium");

  color: c.$text-subtle;
  margin: 0;
  flex-shrink: 0;
}

.invite-composer {
  display: grid;
  gap: 12px;
  margin-top: 12px;
}

.invite-input {
  @include typography.small;

  box-sizing: border-box;
  width: 100%;
  min-height: 72px;
  padding: 9px 12px;
  resize: vertical;
  border: 1px solid c.$border-subtle;
  border-radius: 6px;
  outline: none;
  color: c.$black;
  background-color: c.$white;

  &::placeholder {
    color: c.$text-subtle;
  }

  &:focus {
    border-color: c.$main;
  }

  &.error {
    border-color: c.$text-error-color;
  }
}

.invite-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.invite-message {
  @include typography.small;

  margin: 0;
  flex: 1;

  &.error {
    color: c.$text-error-color;
  }

  &.success {
    color: c.$text-condition-satisfied-color;
  }

  &.hint {
    color: c.$text-subtle;
  }
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 24px;
  border-top: 1px solid c.$border-subtle;
}
</style>
